import {DependencyList, MutableRefObject, useRef} from 'react';

import {RendererOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isShallowEqual} from '../../../../../helpers/reselectHelpers';
import {AsyncDropboxSourceOptions, AsyncDropdownSource} from '../asyncDropdownSource';
import {RowHeightPredicate} from '../dropdownSource';

/*
 * Props.
 */

interface AsyncDropdownSourceState<T, U> {
  source: AsyncDropdownSource<T, U>;
  dependencies: DependencyList;
}

/*
 * Hook.
 */

export function useAsyncDropdownSource<T, U = any>(
  options: AsyncDropboxSourceOptions<T, U>,
  dependencies: DependencyList,
) {
  const dropdownSourceStateRef = useRef<AsyncDropdownSourceState<T, U>>();

  const fetcherRef = useRef(options.fetcher);
  fetcherRef.current = options.fetcher;
  const rowRendererRef = useRef(options.renderRow);
  rowRendererRef.current = options.renderRow;
  const emptyRendererRef = useRef(options.renderEmpty);
  emptyRendererRef.current = options.renderEmpty;
  const rowHeightPredicateRef = useRef(options.rowHeightPredicate);
  rowHeightPredicateRef.current = options.rowHeightPredicate;

  if (
    !dropdownSourceStateRef.current ||
    !isShallowEqual(dropdownSourceStateRef.current.dependencies, dependencies)
  ) {
    dropdownSourceStateRef.current = {
      source: new AsyncDropdownSource<T, U>({
        fetcher: (query, items, cancelToken, paginationData) =>
          fetcherRef.current(query, items, cancelToken, paginationData),
        renderRow: (item, index, items, isSearching) =>
          rowRendererRef.current(item, index, items, isSearching),
        renderEmpty: buildEmptyRenderer(emptyRendererRef),
        rowHeightPredicate: buildRowHeightPredicate(rowHeightPredicateRef),
        initialItems: options.initialItems,
      }),
      dependencies,
    };
  }
  return dropdownSourceStateRef.current.source;
}

/*
 * Helpers.
 */

function buildRowHeightPredicate<T>(
  refFunc: MutableRefObject<RowHeightPredicate<T> | undefined>,
): RowHeightPredicate<T> | undefined {
  if (!refFunc.current) {
    return undefined;
  }
  return (item, index, items, isSearching) => {
    if (refFunc.current) {
      return refFunc.current(item, index, items, isSearching);
    }
    // This should not happen as the rowHeightPredicate should not become undefined after being set.
    return 0;
  };
}

function buildEmptyRenderer(
  refFunc: MutableRefObject<RendererOf<string> | undefined>,
): RendererOf<string> | undefined {
  if (!refFunc.current) {
    return undefined;
  }
  return (query) => {
    if (refFunc.current) {
      return refFunc.current(query);
    }
    // This should not happen as the rowHeightPredicate should not become undefined after being set.
    return null;
  };
}
